import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import PageBanner from 'LuxKit/Banners/PageBanner'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router'
import styled from 'styled-components'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload, TakeoverId } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { offerPageRoutes } from 'takeovers/utils/constants/takeoverRoutes'
import moment from 'moment'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import MaldivesCharterPageBanner from 'takeovers/maldivesCharterTakeover/components/MaldivesCharterPageBanner'
import MaldivesCharterTakeoverBackground from 'takeovers/maldivesCharterTakeover/components/MaldivesCharterTakeoverBackground'
import { useShowMaldivesBanner } from 'takeovers/maldivesCharterTakeover/hooks/useShowMaldivesBanner'
import { isBannerHiddenForPath } from 'lib/routing/routeVisibility'
import { LUX_PLUS_GIFT_CARD_CAMPAIGN } from 'luxPlus/constants/base'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const MaldivesBackground = styled(MaldivesCharterTakeoverBackground)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const Content = styled.div`
  transform: translateZ(0);
`

const Banner = styled.div`
  position: relative;
`

/**
 * Tours landing page and tour offer page routes doesn't show banner when tours-specific takeover is active
 */
const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

export const homePageRoutes: Array<string> = [
  '/:regionCode',
]

const BANNER_DISMISSAL_PERIOD_DAYS = 7

function SiteTakeoverPageBanner() {
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const shouldShowMaldivesBanner = useShowMaldivesBanner()

  // Call all hooks unconditionally
  const siteTakeover = useCurrentSiteTakeover()
  // Check for tours-specific takeover to avoid duplicate banners on tours pages
  const siteTakeoverOnTour = useCurrentSiteTakeover({ heroVertical: 'tours' })

  const labSiteTakeoverTheme = useSiteTakeoverTheme()

  const hide = useMemo((): boolean => {
    // honor custom config excepted routes
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner?.exceptedRoutes ?? EmptyArray
    // honor custom config to show on home page
    const showOnHomePage = siteTakeover?.siteWide?.banner?.showOnHomePage ?? false
    // hide on:
    // - The home page (has its own carousel takeover) - unless showOnHomePage is true
    // - Search pages (has its own banner)
    // - Checkout flow
    // - Booking flow
    // - Custom takeover excepted routes (e.g. lux-plus page)
    // - Tours routes when a tours-specific takeover is active (avoid banner + carousel at the same time)
    return (!showOnHomePage && !shouldShowMaldivesBanner && !!matchPath(currentPath, { path: homePageRoutes, exact: true })) ||
      isBannerHiddenForPath(currentPath, 'siteTakeoverPageBanner') ||
      (!!exceptedRoutes.length && !!matchPath(currentPath, { path: exceptedRoutes })) ||
      (!!siteTakeoverOnTour && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, siteTakeover, siteTakeoverOnTour, shouldShowMaldivesBanner])

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const useLab = !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!siteTakeover?.siteWide?.banner

  // Generate a unique identifier for the current banner configuration
  const bannerIdentifier = useMemo(() => {
    // For lab config, use the title as identifier
    if (siteTakeoverBannerContentPayload) {
      return `siteTakeoverBanner_lab_${siteTakeoverBannerContentPayload.title ?? 'default'}`
    }
    // For custom config, use the takeover ID
    if (siteTakeover?.siteWide?.banner) {
      return `siteTakeoverBanner_custom_${siteTakeover.id ?? 'default'}`
    }
    return 'siteTakeoverBanner_default'
  }, [siteTakeoverBannerContentPayload, siteTakeover])

  const [isDismissed, setIsDismissed] = useState(false)

  // Check dismissal state on mount and when banner identifier changes
  useEffect(() => {
    setIsDismissed(isBannerDismissed(bannerIdentifier))
  }, [bannerIdentifier])

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
    // Dismiss for BANNER_DISMISSAL_PERIOD_DAYS days from now
    recordSiteBannerDismissal(bannerIdentifier, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
  }, [bannerIdentifier])

  // Honor custom takeover config to hide on mobile for offer pages
  const shouldHideOnMobile = useMemo(() => {
    return !!siteTakeover?.siteWide?.banner?.hideOnOfferPagesForMobileScreenSize &&
      !!matchPath(currentPath, { path: offerPageRoutes })
  }, [siteTakeover, currentPath])

  const customSubtitle = useResponsiveSelector({
    mobile: false,
  }) ?? true

  const isDesktop = useResponsiveSelector({
    mobile: false,
  }) ?? true
  const locationIcons = siteTakeoverBannerContentPayload?.locationIcons
  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const locationIcon = locationIcons?.generic
    if (locationIcon) {
      const icon = isDesktop ? locationIcon.desktopIcon : locationIcon.mobileIcon
      if (icon) {
        const iconWidth = isDesktop ? locationIcon.desktopIconDimension?.width : locationIcon.mobileIconDimension?.width
        const iconHeight = isDesktop ? locationIcon.desktopIconDimension?.height : locationIcon.mobileIconDimension?.height
        return {
          id: icon,
          width: iconWidth,
          height: iconHeight,
        }
      }
    }
    const fallbackLogoRef = labSiteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [locationIcons, isDesktop, labSiteTakeoverTheme?.logoRef])

  const { SHOULD_OVERRIDE_LAB_CAMPAIGN } = LUX_PLUS_GIFT_CARD_CAMPAIGN

  if (hide || isDismissed || SHOULD_OVERRIDE_LAB_CAMPAIGN) {
    return null
  }

  // Extract values for lab config
  const title = siteTakeoverBannerContentPayload?.title ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.title) : undefined
  const subtitle = (customSubtitle && labSiteTakeoverTheme?.id === TakeoverId.BEST_OF_2025_TAKEOVER)
    ? 'Explore our most loved limited-time getaways.'
    : siteTakeoverBannerContentPayload?.subtitle
      ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.subtitle)
      : undefined
  const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
  const backgroundStyle = siteTakeoverBannerContentPayload?.backgroundStyle
  // Use 'inverse' palette for Maldives banner (blue background needs light text)
  const paletteType = shouldShowMaldivesBanner ? 'inverse' : (siteTakeoverBannerContentPayload?.palette ?? 'default')

  // Determine banner content based on config type
  // Show Maldives banner for AU users
  const bannerContent = shouldShowMaldivesBanner ? (
    <MaldivesCharterPageBanner />
  ) : useLab ? (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
          <Group direction="vertical" verticalAlign="start">
            <BodyText variant="medium" weight="bold">
              {title}
            </BodyText>
            <BodyText variant="small">
              {subtitle}
              {ctaLink && (
                <>
                  <span style={{ marginRight: rem(4) }} />
                  <TextLink to={ctaLink} wrap="no-wrap">View all</TextLink>
                </>
              )}
            </BodyText>
          </Group>
        </Group>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}

          <BodyText variant="medium">
            <b>{title}</b>
            <span style={{ marginRight: rem(12) }} />
            {subtitle}
          </BodyText>

          {ctaLink && (
            <TextLink to={ctaLink} wrap="no-wrap">View all</TextLink>
          )}
        </Group>
      </CSSBreakpoint>
    </>
  ) : siteTakeover?.siteWide?.banner.description

  // Determine PageBanner props based on config type
  const pageBannerProps = useCustom && siteTakeover?.siteWide?.banner ? {
    primaryActionButtonProps: siteTakeover.siteWide.banner.bannerAction,
    icon: siteTakeover.siteWide.banner.logo,
    align: siteTakeover.siteWide.banner.align ?? 'start',
    tabletAlign: siteTakeover.siteWide.banner.tabletAlign ?? 'center',
  } : {
    align: 'start' as const,
    tabletAlign: 'center' as const,
  }

  // Determine which background to use
  const renderBackground = () => {
    // Override with Maldives background for AU users
    if (shouldShowMaldivesBanner) {
      return <MaldivesBackground placement="siteWideBanner" />
    }
    if (useCustom) {
      return <Background background="default" placement="siteWideBanner" />
    }
    if (useLab && backgroundStyle) {
      return <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />
    }
    return null
  }

  return (
    <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
      <Banner>
        {renderBackground()}
        <Content className={getThemeClassName({
          mobile: useCustom ? (siteTakeover?.siteWide?.banner?.palette ?? siteTakeover?.palette) : paletteType,
        })}
        >
          <PageBanner
            {...pageBannerProps}
            density="super-compact"
            kind="none"
            onDismiss={onDismissHandler}
          >
            {bannerContent}
          </PageBanner>
        </Content>
      </Banner>
    </CSSBreakpoint>
  )
}

export default SiteTakeoverPageBanner
